import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {getAddressesSuggestList} from 'api/geo';
import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {AsyncSelect, SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {editCustomsData$, updateCustomsFx} from 'models/customs/grid/table';

import {Address} from 'types/geo';
import {Customs} from 'types/profile';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type FormField = {
    name: keyof Omit<Customs, 'id'>;
    readonly?: boolean;
    options?: SelectOption<string>[];
};

export const UpdateCustoms = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.customs.create.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.customs.create.fields.' + key), [
        i18n.language,
    ]);

    const editCustomsData = useStore(editCustomsData$);
    useEffect(() => {
        if (!editCustomsData) return history.push(ROUTE.CUSTOMS);

        setValue('number', editCustomsData.number);
        setValue('name', editCustomsData.name);
        setValue('address', editCustomsData.address);
    }, []);

    const schema = useMemo(
        () =>
            yup.object().shape({
                number: yup.string().required(translateValidates('required')),
                name: yup.string().required(translateValidates('required')),
                address: yup.string().required(translateValidates('required')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        reset,
    } = useForm<Customs>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (data: Customs) => {
        try {
            if (!editCustomsData?.id) return;

            await updateCustomsFx({...data, id: editCustomsData.id});
            reset();
            history.push(ROUTE.CUSTOMS);
        } catch (error) {
            console.log(error);
        }
    };

    const formFields: FormField[] = useMemo(
        () => [{name: 'number'}, {name: 'name'}, {name: 'address'}],
        [t],
    );

    const addressOptions = async (partialAddress: string) => {
        try {
            if (!partialAddress) return [{value: '', label: ''}];
            const lang =
                (['ru', 'en'] as Address['lang'][]).find((lang) => lang === i18n.language) || 'ru';
            const payload: Address = {
                address: partialAddress,
                lang,
            };

            const values = (await getAddressesSuggestList(payload)).data;
            return values.map((value) => ({value, label: value}));
        } catch (error) {
            console.error(error);
            return [{value: '', label: ''}];
        }
    };

    const controlRender = (controlEvents: ControllerRenderProps<Customs>, field: FormField) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        if (field.name === 'address') {
            return (
                <div style={{paddingBottom: '6px'}}>
                    <AsyncSelect
                        {...baseProps}
                        name={controlEvents.name}
                        loadOptions={addressOptions}
                        isClearable
                        onChange={(v?: {value: string; label: string}) => {
                            controlEvents.onChange(v?.value || '');
                        }}
                        value={
                            controlEvents.value
                                ? {value: controlEvents.value, label: controlEvents.value}
                                : null
                        }
                    />
                </div>
            );
        }

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.CUSTOMS}>{t('pages.customs.buttons.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.customs.update.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.customs.create.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.CUSTOMS);
                                    reset();
                                }}
                            >
                                {t('components.form.Cancel')}
                            </BackButton>
                            <SubmitButton type="submit">{t('components.form.Save')}</SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
